/**
 * What a join code becomes on a relay, and what it never becomes.
 *
 * A relay carries session descriptions for anyone who asks about a room, so the room name must not
 * be the join code: knowing the name would otherwise be knowing the code. The room id is a hash of
 * the code, and the code itself is handed to Trystero as the AES-GCM password, which means the
 * relay operator sees an opaque topic and ciphertext and can work out neither the code nor who is
 * in the room.
 *
 * That is also why there is no expiry and no registry. A code is live exactly as long as somebody
 * hosts it, because nothing anywhere else remembers it.
 */

/** Trystero's namespace. Bumping it makes old clients invisible rather than incompatible. */
export const NET_APP_ID = 'tbd-coop-1';

/** Half a SHA-256 is 128 bits of room name, far past what a topic string needs to stay unique. */
const ROOM_ID_BYTES = 16;

/**
 * The room id for a canonical join code. Salted with the app id so the same code in a different
 * build is a different room, and so the digest of a bare eight-character string is never useful on
 * its own.
 */
export async function roomIdForCode(canonical: string): Promise<string> {
  const source = new TextEncoder().encode(`${NET_APP_ID}:${canonical}`);
  const digest = await crypto.subtle.digest('SHA-256', source);
  return [...new Uint8Array(digest).slice(0, ROOM_ID_BYTES)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * The encryption password for a code, which is the code. It never leaves this machine: Trystero
 * derives a key from it and encrypts the session descriptions before they reach a relay.
 */
export function roomPasswordForCode(canonical: string): string {
  return canonical;
}
